import { useRef, useState } from "react"
import { useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { NIGERIAN_STATES } from "@client/lib/nigerian-states"
import { uploadFile, type UploadedFile } from "@client/lib/uploads"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { Select } from "./components/select"
import { DatePicker } from "./components/date-picker"

type Step = "basics" | "disability" | "contact" | "support" | "documents" | "review"

const STEPS: { key: Step; label: string }[] = [
  { key: "basics", label: "Basics" },
  { key: "disability", label: "Disability profile" },
  { key: "contact", label: "Contact & location" },
  { key: "support", label: "Support plan" },
  { key: "documents", label: "Documents" },
  { key: "review", label: "Review" },
]

const GENDERS = ["Male", "Female"] as const
const AGE_GROUPS = ["Child", "Teen", "University", "Adult"] as const
const CATEGORIES = ["Mobility", "Visual", "Hearing", "Limb difference", "Other"] as const
const RELATIONSHIPS = ["Parent", "Guardian", "Spouse", "Sibling", "Caregiver", "Self", "Other"] as const

function BackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  )
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  )
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

interface FormData {
  // Basics
  fullName: string
  dateOfBirth: string
  gender: string
  ageGroup: string
  about: string
  photo: UploadedFile | null
  photoPreview: string | null
  // Disability profile
  category: string
  condition: string
  onset: string
  aidsInUse: string
  clinicTherapist: string
  medicalNotes: string
  // Contact & location
  contactName: string
  contactPhone: string
  contactRelationship: string
  state: string
  lga: string
  // Support plan
  institution: string
  institutionRole: string
  accessibilityNeeds: string
  plannedSupportItems: string
  // Documents
  documents: { name: string; file: UploadedFile }[]
}

const initialFormData: FormData = {
  fullName: "",
  dateOfBirth: "",
  gender: "Male",
  ageGroup: "Child",
  about: "",
  photo: null,
  photoPreview: null,
  category: "Mobility",
  condition: "",
  onset: "",
  aidsInUse: "",
  clinicTherapist: "",
  medicalNotes: "",
  contactName: "",
  contactPhone: "",
  contactRelationship: "Parent",
  state: "Edo",
  lga: "",
  institution: "",
  institutionRole: "",
  accessibilityNeeds: "",
  plannedSupportItems: "",
  documents: [],
}

function categoryToValue(label: string): "mobility" | "visual" | "hearing" | "limb_difference" | "other" {
  const map: Record<string, "mobility" | "visual" | "hearing" | "limb_difference" | "other"> = {
    Mobility: "mobility",
    Visual: "visual",
    Hearing: "hearing",
    "Limb difference": "limb_difference",
    Other: "other",
  }
  return map[label] ?? "mobility"
}

function ageGroupToValue(label: string): "child" | "teen" | "university" | "adult" {
  const map: Record<string, "child" | "teen" | "university" | "adult"> = {
    Child: "child",
    Teen: "teen",
    University: "university",
    Adult: "adult",
  }
  return map[label] ?? "child"
}

export function AddAbilityPage() {
  const navigate = useNavigate()
  const canMutate = useHasPermission("ability_mutate")
  const [currentStep, setCurrentStep] = useState<Step>("basics")
  const [form, setForm] = useState<FormData>(initialFormData)
  const [saving, setSaving] = useState(false)
  const [photoUploading, setPhotoUploading] = useState(false)
  const [documentUploading, setDocumentUploading] = useState(false)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const documentInputRef = useRef<HTMLInputElement>(null)

  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep)
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === STEPS.length - 1

  const handlePrevious = () => {
    if (!isFirstStep && currentStepIndex > 0) {
      const prevStep = STEPS[currentStepIndex - 1]
      if (prevStep) setCurrentStep(prevStep.key)
    }
  }

  const handleNext = () => {
    if (!isLastStep && currentStepIndex < STEPS.length - 1) {
      const nextStep = STEPS[currentStepIndex + 1]
      if (nextStep) setCurrentStep(nextStep.key)
    }
  }

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file || photoUploading) return

    setForm((current) => ({ ...current, photo: null }))
    const reader = new FileReader()
    reader.onloadend = () => {
      setForm((current) => ({
        ...current,
        photoPreview: reader.result as string,
      }))
    }
    reader.readAsDataURL(file)

    setPhotoUploading(true)
    try {
      const uploaded = await uploadFile(file, "ability-photo")
      setForm((current) => ({ ...current, photo: uploaded }))
      toast.success("Photo uploaded")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload photo")
    } finally {
      setPhotoUploading(false)
    }
  }

  const handleDocumentAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ""
    if (files.length === 0 || documentUploading) return

    setDocumentUploading(true)
    try {
      const results = await Promise.allSettled(files.map((file) => uploadFile(file, "ability-document")))
      const uploadedDocuments = results.flatMap((result) =>
        result.status === "fulfilled" ? [{ name: result.value.name, file: result.value }] : [],
      )
      const failedCount = results.length - uploadedDocuments.length

      if (uploadedDocuments.length > 0) {
        setForm((current) => ({
          ...current,
          documents: [...current.documents, ...uploadedDocuments],
        }))
        toast.success(`${uploadedDocuments.length} document${uploadedDocuments.length === 1 ? "" : "s"} uploaded`)
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} document${failedCount === 1 ? "" : "s"} could not be uploaded`)
      }
    } finally {
      setDocumentUploading(false)
    }
  }

  const handleDocumentRemove = (index: number) => {
    setForm({
      ...form,
      documents: form.documents.filter((_, i) => i !== index),
    })
  }

  const validateForm = (): string | null => {
    if (!form.fullName.trim()) return "Full name is required"
    if (!form.dateOfBirth) return "Date of birth is required"
    if (!form.condition.trim()) return "Condition is required"
    if (!form.state) return "State is required"
    return null
  }

  const handleSubmit = async () => {
    const validationError = validateForm()
    if (validationError) {
      toast.error(validationError)
      return
    }

    setSaving(true)
    try {
      const photo = form.photo
      const documents = form.documents.map((document) => ({
        ...document.file,
        purpose: "ability-document" as const,
      }))

      const res = await client.api.admin.ability.$post({
        json: {
          fullName: form.fullName.trim(),
          dateOfBirth: form.dateOfBirth,
          gender: form.gender.toLowerCase() as "male" | "female",
          ageGroup: ageGroupToValue(form.ageGroup),
          category: categoryToValue(form.category),
          condition: form.condition.trim(),
          onset: form.onset.trim() || undefined,
          aidsInUse: form.aidsInUse.trim() || undefined,
          clinicTherapist: form.clinicTherapist.trim() || undefined,
          medicalNotes: form.medicalNotes.trim() || undefined,
          contactName: form.contactName.trim() || undefined,
          contactPhone: form.contactPhone.trim() || undefined,
          contactRelationship: form.contactRelationship || undefined,
          state: form.state,
          lga: form.lga.trim() || undefined,
          about: form.about.trim() || undefined,
          institution: form.institution.trim() || undefined,
          institutionRole: form.institutionRole.trim() || undefined,
          accessibilityNeeds: form.accessibilityNeeds.trim() || undefined,
          plannedSupportItems: form.plannedSupportItems.trim() || undefined,
          photoKey: photo?.key,
          documents,
        },
      })

      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }

      if ("data" in body) {
        toast.success("Beneficiary enrolled successfully")
        navigate(`/admin/ability/${body.data.id}`)
      }
    } catch {
      toast.error("Failed to create beneficiary")
    } finally {
      setSaving(false)
    }
  }

  if (!canMutate) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Add Beneficiary</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to add beneficiaries.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/admin/ability")} className="text-ink/60 hover:text-ink">
          <BackIcon className="h-5 w-5" />
        </button>
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-3xl">Add Beneficiary</h1>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        {STEPS.map((step, index) => {
          const isActive = step.key === currentStep
          const isCompleted = index < currentStepIndex
          return (
            <button
              key={step.key}
              onClick={() => setCurrentStep(step.key)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-forest-950 text-white"
                  : isCompleted
                    ? "bg-forest-950/10 text-forest-950"
                    : "text-ink/40 hover:text-ink/60"
              }`}
            >
              {isCompleted && <CheckIcon className="h-4 w-4" />}
              <span>{index + 1}. {step.label}</span>
            </button>
          )
        })}
      </div>

      {/* Form Content */}
      <div className="rounded-[10px] border border-gold-500 bg-white p-6">
        {currentStep === "basics" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-forest-950">Basic Information</h2>
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-ink">Full name *</label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Enter full name"
                />
              </div>
              <DatePicker
                label="Date of birth *"
                value={form.dateOfBirth}
                onChange={(value) => setForm({ ...form, dateOfBirth: value })}
              />
              <Select
                label="Gender *"
                value={form.gender}
                onChange={(value) => setForm({ ...form, gender: value })}
                options={GENDERS}
              />
              <Select
                label="Age group *"
                value={form.ageGroup}
                onChange={(value) => setForm({ ...form, ageGroup: value })}
                options={AGE_GROUPS}
              />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">Photo</label>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                {form.photoPreview ? (
                  <div className="flex items-center gap-4">
                    <img src={form.photoPreview} alt="Preview" className="h-16 w-16 rounded-full object-cover" />
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="text-sm text-forest-700 hover:underline"
                      disabled={photoUploading}
                    >
                      {photoUploading ? "Uploading..." : "Change photo"}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="flex items-center gap-2 rounded-lg border border-dashed border-gold-500 bg-cream-50 px-4 py-3 text-sm text-ink/60 hover:border-forest-700 hover:text-ink"
                    disabled={photoUploading}
                  >
                    <UploadIcon className="h-4 w-4" />
                    {photoUploading ? "Uploading..." : "Upload photo"}
                  </button>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-ink">About</label>
                <textarea
                  value={form.about}
                  onChange={(e) => setForm({ ...form, about: e.target.value })}
                  rows={4}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Brief description of the beneficiary's situation"
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === "disability" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-forest-950">Disability Profile</h2>
            <div className="grid gap-6 sm:grid-cols-2">
              <Select
                label="Category *"
                value={form.category}
                onChange={(value) => setForm({ ...form, category: value })}
                options={CATEGORIES}
              />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">Condition *</label>
                <input
                  type="text"
                  value={form.condition}
                  onChange={(e) => setForm({ ...form, condition: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="e.g., Cerebral palsy, Low vision"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">Onset</label>
                <input
                  type="text"
                  value={form.onset}
                  onChange={(e) => setForm({ ...form, onset: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="e.g., At birth, Age 5"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">Aids in use</label>
                <input
                  type="text"
                  value={form.aidsInUse}
                  onChange={(e) => setForm({ ...form, aidsInUse: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="e.g., Wheelchair, Hearing aid"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">Clinic / Therapist</label>
                <input
                  type="text"
                  value={form.clinicTherapist}
                  onChange={(e) => setForm({ ...form, clinicTherapist: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Name of clinic or therapist"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-ink">Medical notes</label>
                <textarea
                  value={form.medicalNotes}
                  onChange={(e) => setForm({ ...form, medicalNotes: e.target.value })}
                  rows={4}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Any relevant medical information"
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === "contact" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-forest-950">Contact & Location</h2>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">Contact name</label>
                <input
                  type="text"
                  value={form.contactName}
                  onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Name of primary contact"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">Contact phone</label>
                <input
                  type="tel"
                  value={form.contactPhone}
                  onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Phone number"
                />
              </div>
              <Select
                label="Relationship"
                value={form.contactRelationship}
                onChange={(value) => setForm({ ...form, contactRelationship: value })}
                options={RELATIONSHIPS}
              />
              <Select
                label="State *"
                value={form.state}
                onChange={(value) => setForm({ ...form, state: value })}
                options={NIGERIAN_STATES}
              />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">LGA</label>
                <input
                  type="text"
                  value={form.lga}
                  onChange={(e) => setForm({ ...form, lga: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="Local government area"
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === "support" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-forest-950">Support Plan</h2>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">Institution</label>
                <input
                  type="text"
                  value={form.institution}
                  onChange={(e) => setForm({ ...form, institution: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="School, university, or workplace"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-ink">Role / Level</label>
                <input
                  type="text"
                  value={form.institutionRole}
                  onChange={(e) => setForm({ ...form, institutionRole: e.target.value })}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="e.g., Primary 3, 200 Level, Employee"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-ink">Accessibility needs</label>
                <textarea
                  value={form.accessibilityNeeds}
                  onChange={(e) => setForm({ ...form, accessibilityNeeds: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="What accessibility accommodations are needed?"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-ink">Planned support items</label>
                <textarea
                  value={form.plannedSupportItems}
                  onChange={(e) => setForm({ ...form, plannedSupportItems: e.target.value })}
                  rows={3}
                  className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="e.g., Wheelchair, school fees, therapy sessions"
                />
              </div>
            </div>
          </div>
        )}

        {currentStep === "documents" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-forest-950">Documents</h2>
            <input
              ref={documentInputRef}
              type="file"
              accept=".pdf,image/*"
              multiple
              onChange={handleDocumentAdd}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => documentInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-gold-500 bg-cream-50 px-4 py-8 text-sm text-ink/60 hover:border-forest-700 hover:text-ink"
              disabled={documentUploading}
            >
              <UploadIcon className="h-5 w-5" />
              {documentUploading ? "Uploading..." : "Upload documents (PDF, images)"}
            </button>

            {form.documents.length > 0 && (
              <ul className="space-y-2">
                {form.documents.map((doc, index) => (
                  <li key={index} className="flex items-center justify-between rounded-lg border border-gold-500 bg-cream-50 px-4 py-3">
                    <span className="truncate text-sm text-ink">{doc.name}</span>
                    <button
                      type="button"
                      onClick={() => handleDocumentRemove(index)}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {currentStep === "review" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-forest-950">Review & Submit</h2>
            <div className="grid gap-6 lg:grid-cols-2">
              <section className="rounded-lg border border-gold-500/50 p-4">
                <h3 className="mb-3 font-medium text-forest-950">Basic Information</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-ink/60">Full name</dt><dd className="font-medium">{form.fullName || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">Date of birth</dt><dd className="font-medium">{form.dateOfBirth ? new Date(form.dateOfBirth).toLocaleDateString() : "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">Gender</dt><dd className="font-medium">{form.gender}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">Age group</dt><dd className="font-medium">{form.ageGroup}</dd></div>
                </dl>
              </section>

              <section className="rounded-lg border border-gold-500/50 p-4">
                <h3 className="mb-3 font-medium text-forest-950">Disability Profile</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-ink/60">Category</dt><dd className="font-medium">{form.category}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">Condition</dt><dd className="font-medium">{form.condition || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">Onset</dt><dd className="font-medium">{form.onset || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">Aids in use</dt><dd className="font-medium">{form.aidsInUse || "—"}</dd></div>
                </dl>
              </section>

              <section className="rounded-lg border border-gold-500/50 p-4">
                <h3 className="mb-3 font-medium text-forest-950">Contact & Location</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-ink/60">Contact name</dt><dd className="font-medium">{form.contactName || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">Contact phone</dt><dd className="font-medium">{form.contactPhone || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">State</dt><dd className="font-medium">{form.state}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">LGA</dt><dd className="font-medium">{form.lga || "—"}</dd></div>
                </dl>
              </section>

              <section className="rounded-lg border border-gold-500/50 p-4">
                <h3 className="mb-3 font-medium text-forest-950">Support Plan</h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between"><dt className="text-ink/60">Institution</dt><dd className="font-medium">{form.institution || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">Role</dt><dd className="font-medium">{form.institutionRole || "—"}</dd></div>
                  <div className="flex justify-between"><dt className="text-ink/60">Documents</dt><dd className="font-medium">{form.documents.length} files</dd></div>
                </dl>
              </section>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={isFirstStep}
          className="flex items-center gap-2 rounded-full border border-gold-500 px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-cream-100 disabled:opacity-50"
        >
          <BackIcon className="h-4 w-4" />
          Previous
        </button>
        <div className="flex gap-3">
          {isLastStep ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 rounded-full bg-forest-950 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900 disabled:opacity-50"
            >
              {saving ? "Enrolling..." : "Enroll Beneficiary"}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              className="flex items-center gap-2 rounded-full bg-forest-950 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
            >
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
